import React, {useCallback, useState} from 'react';
import PropTypes from 'prop-types';
import {Modal, FileUpload} from '@/components/atoms';
import {MODAL_TYPE} from '@/utils/constants/globalStyle';
import Papa from 'papaparse';
import {useCsvUploadModal} from '@/hooks/useCsvUploadModal';

const hasValidLeaderboardFilePrefix = (fileName, period) => {
  const normalizedFileName = String(fileName || '')
    .trim()
    .toLowerCase();
  const normalizedPeriod = String(period || '')
    .trim()
    .toLowerCase();

  return normalizedFileName.startsWith(`${normalizedPeriod}_leaderboard_`);
};

const validateCSVData = (data, headers) => {
  const trimmedHeaders = headers.map((h) => h.trim());
  const trimmedHeadersSet = new Set(trimmedHeaders);

  const requiredColumns = ['Points'];
  const allowedColumnsSet = new Set(requiredColumns);

  const unexpectedColumns = trimmedHeaders.filter(
    (h) => !allowedColumnsSet.has(h)
  );

  const missingColumns = requiredColumns.filter(
    (col) => !trimmedHeadersSet.has(col)
  );

  const errorMessages = [];

  if (unexpectedColumns.length > 0) {
    errorMessages.push(
      `Unexpected column(s) found: ${unexpectedColumns.join(', ')}`
    );
  }

  if (missingColumns.length > 0) {
    errorMessages.push(
      `Missing required column(s): ${missingColumns.join(', ')}`
    );
  }

  if (errorMessages.length > 0) {
    throw new Error(errorMessages.join('. '));
  }

  const pointsColumn = headers.find((h) => h.trim() === 'Points');

  if (!pointsColumn) {
    throw new Error('CSV must contain "Points" column');
  }

  const formattedData = [];
  const errors = [];

  data.forEach((row, index) => {
    const points = row[pointsColumn];

    if (!points || String(points).trim() === '') {
      errors.push(`Row ${index + 1}: 'Points' is required`);
      return;
    }

    const pointsNum = Number(points);
    if (
      Number.isNaN(pointsNum) ||
      !Number.isInteger(pointsNum) ||
      pointsNum < 0
    ) {
      errors.push(`Row ${index + 1}: 'Points' must be a positive integer`);
      return;
    }

    formattedData.push({
      rank: index + 1,
      points: Number.parseInt(points, 10)
    });
  });

  if (errors.length > 0) {
    throw new Error(errors.join(', '));
  }

  if (formattedData.length === 0) {
    throw new Error('No valid data found in CSV');
  }

  return formattedData;
};

const LeaderboardUploadModal = ({
  isOpen,
  onClose,
  onUpload,
  onDone,
  onDelete,
  period,
  existingFileName
}) => {
  const {
    uploadError,
    isParsingFile,
    isUploading,
    isUploadComplete,
    uploadedFileName,
    handleClose,
    handleFileDelete: hookHandleFileDelete,
    handleDone,
    processFile
  } = useCsvUploadModal({isOpen, existingFileName});

  const [invalidTypeError, setInvalidTypeError] = useState('');

  const handleFileDelete = useCallback(() => {
    hookHandleFileDelete();
    setInvalidTypeError('');
    if (onDelete) onDelete();
  }, [hookHandleFileDelete, onDelete]);

  const parseFile = useCallback(
    (file) =>
      new Promise((resolve, reject) => {
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            try {
              const {data, meta} = results;
              const headers = meta.fields || [];
              resolve(validateCSVData(data, headers));
            } catch (error) {
              reject(error instanceof Error ? error : new Error(String(error)));
            }
          },
          error: () => reject(new Error('Error reading CSV file'))
        });
      }),
    []
  );

  const handleFileSelect = useCallback(
    (file) => {
      if (!file) return;
      setInvalidTypeError('');
      processFile(file, {
        preValidate: (f) => {
          if (!hasValidLeaderboardFilePrefix(f.name, period)) {
            return `Selected file name must start with ${period}_Leaderboard_ for the ${period} tab`;
          }
          return null;
        },
        parseFile,
        onUpload
      });
    },
    [parseFile, onUpload, processFile, period]
  );

  return (
    <Modal
      isDark={true}
      onCloseModal={() => handleClose(onClose)}
      onClickSecondaryButton={() => handleClose(onClose)}
      onClickPrimaryButton={() => handleDone(onDone, onClose)}
      showModal={isOpen}
      title={`Upload ${period} Leaderboard`}
      primaryButtonText='Done'
      primaryButtonDisabled={!isUploadComplete || isParsingFile || isUploading}
      secondaryButtonText='Cancel'
      type={MODAL_TYPE.SMALL}
      showSecondaryButton={true}
      content={
        <div className='flex flex-col gap-4'>
          <FileUpload
            acceptedExtensions='.csv'
            description='Drag and drop your CSV file here (only .csv)'
            label='Leaderboard CSV File'
            onUpload={handleFileSelect}
            onUploadDelete={handleFileDelete}
            onUploadError={(error) => {
              if (error)
                setInvalidTypeError(
                  'Invalid file format. Only CSV files are allowed.'
                );
            }}
            helperText={invalidTypeError}
            feedbackType={invalidTypeError ? 'error' : 'info'}
            value={[]}
            multiple={false}
            isDark={true}
            className='!max-w-full'
            containerClassName='!w-full'
            dataTestId='leaderboard-file-upload'
          />

          {!uploadError && isUploading && (
            <div
              className='rounded p-2'
              style={{
                backgroundColor: 'var(--color-background-layer-02)'
              }}>
              <span className='text-[12px] text-[#DADADA]'>Uploading...</span>
            </div>
          )}

          {!uploadError && isUploadComplete && uploadedFileName && (
            <div
              className='flex items-center justify-between rounded px-3 py-2'
              style={{
                backgroundColor: 'var(--color-background-layer-02)'
              }}>
              <div className='flex min-w-0 items-center gap-2'>
                <span className='material-icons flex-shrink-0 text-[18px] text-[#1B9FFF]'>
                  description
                </span>
                <span className='truncate text-[14px] text-[#DADADA]'>
                  {uploadedFileName}
                </span>
              </div>

              <button
                onClick={handleFileDelete}
                className='ml-2 flex-shrink-0 text-[#69B1FF] hover:text-[#8BC4FF]'
                aria-label='Remove uploaded file'>
                <span className='material-icons text-[20px]'>
                  delete_outline
                </span>
              </button>
            </div>
          )}

          {uploadError && (
            <div className='flex items-start gap-2'>
              <span className='material-icons mt-0.5 flex-shrink-0 text-[18px] text-[color:var(--color-feedback-error-high-contrast)]'>
                error
              </span>
              <p className='text-[13px] text-[color:var(--color-feedback-error-high-contrast)]'>
                {uploadError}
              </p>
            </div>
          )}
        </div>
      }
    />
  );
};

LeaderboardUploadModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  onDone: PropTypes.func,
  onDelete: PropTypes.func,
  existingFileName: PropTypes.string,
  period: PropTypes.string.isRequired
};

LeaderboardUploadModal.defaultProps = {
  onDone: () => {},
  onDelete: () => {},
  existingFileName: ''
};

export default React.memo(LeaderboardUploadModal);
